import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { reviewsAPI, jobsAPI } from '../api';

const css = `
.review-wrap { max-width:520px; margin:0 auto; padding:48px 20px 80px; }
.review-card { background:#0F1A2A; border:1px solid rgba(30,127,212,0.15); border-radius:14px; padding:32px; }
.review-title { font-family:'Barlow Condensed',sans-serif; font-size:28px; font-weight:800; color:#F0F6FF; margin-bottom:6px; }
.review-sub { color:#8899AA; font-size:14px; margin-bottom:28px; }
.job-info-box { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.07); border-radius:8px; padding:14px 16px; margin-bottom:24px; }
.job-info-row { display:flex; justify-content:space-between; font-size:13px; color:#8899AA; margin-bottom:4px; }
.job-info-row span { color:#F0F6FF; font-weight:500; }
.stars-wrap { display:flex; gap:6px; margin:8px 0 20px; align-items:center; }
.star-btn { font-size:36px; background:none; border:none; cursor:pointer; transition:transform 0.15s,filter 0.15s; padding:0; line-height:1; }
.star-btn.on { transform:scale(1.15); filter:none; }
.star-btn.off { filter:grayscale(1) opacity(0.3); }
.star-label { font-size:13px; color:#6DBE45; font-weight:600; margin-left:6px; }
.category-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; margin-bottom:20px; }
.cat-item { display:flex; flex-direction:column; gap:5px; }
.cat-label { font-size:11px; text-transform:uppercase; letter-spacing:1px; color:#556677; font-weight:600; }
.cat-stars { display:flex; gap:2px; }
.cat-star { font-size:20px; cursor:pointer; line-height:1; background:none; border:none; padding:0; transition:transform 0.1s; }
.form-group { margin-bottom:20px; }
.form-label { display:block; font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:1px; color:#556677; margin-bottom:8px; }
textarea { width:100%; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); border-radius:8px; padding:12px 14px; color:#F0F6FF; font-size:14px; font-family:'DM Sans',sans-serif; resize:vertical; min-height:100px; outline:none; box-sizing:border-box; }
textarea:focus { border-color:#1E7FD4; }
.would-hire { display:flex; gap:10px; margin-bottom:20px; }
.hire-btn { flex:1; padding:10px; border-radius:8px; border:1px solid rgba(255,255,255,0.1); background:rgba(255,255,255,0.03); color:#8899AA; font-size:13px; font-weight:600; cursor:pointer; transition:all 0.2s; }
.hire-btn.yes.sel { background:rgba(109,190,69,0.15); border-color:#6DBE45; color:#6DBE45; }
.hire-btn.no.sel { background:rgba(255,71,87,0.12); border-color:#FF4757; color:#FF4757; }
.submit-btn { width:100%; padding:14px; background:#1E7FD4; color:#fff; border:none; border-radius:8px; font-size:15px; font-weight:700; cursor:pointer; transition:all 0.2s; display:flex; align-items:center; justify-content:center; gap:8px; }
.submit-btn:hover:not(:disabled) { background:#2E8FE4; }
.submit-btn:disabled { opacity:0.5; cursor:not-allowed; }
.alert-error { background:rgba(255,71,87,0.1); border:1px solid rgba(255,71,87,0.3); color:#FF6B7A; padding:10px 14px; border-radius:8px; font-size:13px; margin-bottom:16px; }
.done-wrap { text-align:center; padding:80px 20px; }
.done-icon { font-size:64px; margin-bottom:16px; }
.done-title { font-family:'Barlow Condensed',sans-serif; font-size:32px; font-weight:800; color:#F0F6FF; margin-bottom:8px; }
.done-sub { color:#8899AA; font-size:15px; margin-bottom:32px; line-height:1.6; }
.spinner { width:16px; height:16px; border:2px solid rgba(255,255,255,0.3); border-top-color:#fff; border-radius:50%; animation:spin 0.7s linear infinite; display:inline-block; }
@keyframes spin { to { transform:rotate(360deg); } }
`;

const STAR_LABELS = ['','Poor','Fair','Good','Great','Excellent'];
const CATEGORIES = [
  { key:'professionalism', label:'Professionalism' },
  { key:'technical_skill', label:'Technical Skill' },
  { key:'communication', label:'Communication' },
  { key:'timeliness', label:'Timeliness' },
];

export default function ReviewPage() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [catRatings, setCatRatings] = useState({professionalism:0,technical_skill:0,communication:0,timeliness:0});
  const [comment, setComment] = useState('');
  const [wouldHire, setWouldHire] = useState(null);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (id) jobsAPI?.get?.(id).then(r => setJob(r.data)).catch(() => {});
  }, [id]);

  const submit = async () => {
    if (!rating) { setErr('Please select an overall rating'); return; }
    setBusy(true); setErr('');
    try {
      await reviewsAPI.submit({ job_id:id, rating, comment, would_hire_again:wouldHire, category_ratings:catRatings });
      setDone(true);
    } catch(e) { setErr(e.response?.data?.error || 'Failed to submit review'); }
    setBusy(false);
  };

  if (done) return (
    <><style>{css}</style>
    <div className="done-wrap">
      <div className="done-icon">⭐</div>
      <div className="done-title">Thank You!</div>
      <div className="done-sub">Your review has been submitted and the provider has been notified.<br/>Your feedback helps build trust in the community.</div>
      <button className="submit-btn" style={{maxWidth:220,margin:'0 auto'}} onClick={()=>nav('/dashboard')}>Back to My Jobs</button>
    </div></>
  );

  return (
    <><style>{css}</style>
    <div className="review-wrap">
      <div className="review-card">
        <div className="review-title">Leave a Review</div>
        <div className="review-sub">Your honest feedback helps build trust in the community</div>

        {job && (
          <div className="job-info-box">
            <div className="job-info-row">Job ID <span>#{id?.slice(0,8).toUpperCase()}</span></div>
            <div className="job-info-row">Provider <span>{job.provider_code || 'FF-XXXX'}</span></div>
            <div className="job-info-row">Market <span>{job.market_name || '—'}</span></div>
          </div>
        )}

        {err && <div className="alert-error">{err}</div>}

        {/* Overall Rating */}
        <div className="form-group">
          <div className="form-label">Overall Rating</div>
          <div className="stars-wrap">
            {[1,2,3,4,5].map(n => (
              <button key={n} type="button"
                className={`star-btn ${(hover||rating)>=n?'on':'off'}`}
                onClick={()=>setRating(n)}
                onMouseEnter={()=>setHover(n)}
                onMouseLeave={()=>setHover(0)}>⭐</button>
            ))}
            <span className="star-label">{STAR_LABELS[hover||rating]}</span>
          </div>
        </div>

        {/* Category Ratings */}
        <div className="form-group">
          <div className="form-label">Rate Specific Areas</div>
          <div className="category-grid">
            {CATEGORIES.map(cat => (
              <div key={cat.key} className="cat-item">
                <div className="cat-label">{cat.label}</div>
                <div className="cat-stars">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" className="cat-star"
                      style={{filter:catRatings[cat.key]>=n?'none':'grayscale(1) opacity(0.3)'}}
                      onClick={()=>setCatRatings(p=>({...p,[cat.key]:n}))}>⭐</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Would Hire Again */}
        <div className="form-group">
          <div className="form-label">Would you hire this provider again?</div>
          <div className="would-hire">
            <button type="button" className={`hire-btn yes ${wouldHire===true?'sel':''}`} onClick={()=>setWouldHire(true)}>👍 Yes, definitely</button>
            <button type="button" className={`hire-btn no ${wouldHire===false?'sel':''}`} onClick={()=>setWouldHire(false)}>👎 No</button>
          </div>
        </div>

        {/* Comment */}
        <div className="form-group">
          <div className="form-label">Written Feedback <span style={{color:'#445566',fontWeight:400,textTransform:'none',letterSpacing:0}}>(optional)</span></div>
          <textarea placeholder="Describe your experience — what went well, what could be improved..."
            value={comment} onChange={e=>setComment(e.target.value)} rows={4}/>
        </div>

        <button className="submit-btn" onClick={submit} disabled={busy||!rating}>
          {busy?<><span className="spinner"/> Submitting...</>:'Submit Review →'}
        </button>
      </div>
    </div></>
  );
}
